import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useLayersChange from "../../../../hooks/useLayersChange";
import { ConfigContext } from "../../../../utils/ConfigContext";
import { getLayerSymbology } from "../../../../utils/symbologies";
import { Tooltip } from "../../../UI";
import {
  StyledContainer,
  StyledMetricsContainer,
  StyledMetricsGrid,
  StyledMetricsTag,
  StyledMetricsTagContainer,
} from "./EditorMetrics-styled";

const EditorMetrics = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [layersWithUpdates, setLayersWithUpdates] = useState(0);
  const reports = useSelector((state) => state.dashboard.reports);
  const layer = useSelector((state) => state.dashboard.reportsLayer);
  const view = useSelector((state) => state.mapViewReducer.view);

  const operationLayers = useSelector(
    (state) => state.mapViewReducer.operationLayers
  );

  const { layerCount, visibleLayerConfigsIds } = useLayersChange(view);

  useEffect(() => {
    let timer;
    if (!layer || !view) return;

    timer = setTimeout(() => {
      try {
        setIsVisible(!!visibleLayerConfigsIds[layer.layerConfig.id]);
        const layersWithReportUpdate = view.map.layers.filter(
          (l) => l.layerConfig && l.layerConfig.hasReportingUpdate && l.visible
        );
        setLayersWithUpdates(layersWithReportUpdate.length);
      } catch (err) {}
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [layerCount, layer, view]);

  const hasReports = useMemo(() => {
    let hasPacReports = false;
    operationLayers.forEach((item) => {
      const isPacReport =
        item.layerConfig.alias === "pacReports" ||
        item.layerConfig.extends === "pacReports";
      if (!hasPacReports) {
        hasPacReports = isPacReport;
      }
    });

    return hasPacReports;
  }, [operationLayers]);

  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");

  const colors = useMemo(() => {
    if (!layer) return {};
    const { colorMap = {} } = getLayerSymbology(layer, config) || {};
    if (!colorMap.default) return {};

    const filterColors = {};
    Object.keys(colorMap.default).forEach((code) => {
      const color = colorMap.default[code];
      if (color) {
        filterColors[code] = {
          code: Number(code),
          color,
          label: t(`layer.domain.currvalidationfield.${code}`),
        };
      }
    });
    return filterColors;
  }, [config, view, layer, t]);

  const reportsMetrics = useMemo(() => {
    const standAloneReports = {
      1: 0,
    };

    const updateReports = {
      1: 0,
    };

    try {
      if (!reports || reports.length === 0)
        return { reports: standAloneReports, updateReports };

      reports.forEach((report) => {
        const status = report.attributes?.currvalidationfield;
        const reporttype = report.attributes?.reporttype;
        const reportedLayerId = report.attributes?.reportedlayerid;

        if (reporttype === 1) {
          if (status in standAloneReports) {
            standAloneReports[status]++;
          }
        } else if (reportedLayerId) {
          if (status in updateReports) {
            updateReports[status]++;
          }
        }
      });
    } catch (error) {
      //  log(error);
    }
    return {
      reports: standAloneReports,
      updateReports,
    };
  }, [reports]);

  if (!layer) return null;

  if (layersWithUpdates === 0 && !isVisible) return null;

  return (
    <StyledContainer>
      <StyledMetricsGrid columns={2}>
        {hasReports && isVisible && (
          <Tooltip
            placement="top"
            content={t(
              "screen.widget.Editor.manager.reports.pacReportsInformation"
            )}
            showOnHover
            blurred
            wrapperStyles={{
              cursor: "default",
            }}
          >
            <StyledMetricsContainer>
              <StyledMetricsTagContainer>
                {Object.keys(reportsMetrics.reports).map((key) => {
                  const count = reportsMetrics.reports[key];

                  return (
                    <StyledMetricsTag
                      key={key}
                      background={
                        count === 0
                          ? "var(--elementBackgroundTransparent)"
                          : colors[key]?.color || "var(--elementBackground)"
                      }
                      color={count === 0 ? "var(--fontColor)" : ""}
                    >
                      {count} {colors[key]?.label || ""}
                    </StyledMetricsTag>
                  );
                })}
              </StyledMetricsTagContainer>
              <span>{t("layer.title.pacReports.title")}</span>
            </StyledMetricsContainer>
          </Tooltip>
        )}
        <Tooltip
          placement="top"
          content={t("screen.widget.Editor.manager.reports.updatesInformation")}
          showOnHover
          blurred
          wrapperStyles={{
            cursor: "default",
          }}
        >
          <StyledMetricsContainer>
            <StyledMetricsTagContainer>
              {Object.keys(reportsMetrics.updateReports).map((key) => {
                const count = reportsMetrics.updateReports[key];

                return (
                  <StyledMetricsTag
                    key={key}
                    background={
                      count === 0
                        ? "var(--elementBackgroundTransparent)"
                        : colors[key]?.color || "var(--elementBackground)"
                    }
                    color={count === 0 ? "var(--fontColor)" : ""}
                  >
                    {count} {colors[key]?.label || ""}
                  </StyledMetricsTag>
                );
              })}
            </StyledMetricsTagContainer>
            <span>
              {t("screen.widget.Editor.manager.reports.updatesKpiTitle")}
            </span>
          </StyledMetricsContainer>
        </Tooltip>
      </StyledMetricsGrid>
    </StyledContainer>
  );
};

export default EditorMetrics;
